import type { CSSProperties } from 'react';

export type IconLogoProps = {
  animated?: boolean;
  animationState?: 'entering' | 'exiting' | 'idle';
  height?: number | string;
  style?: CSSProperties;
  width?: number | string;
};

const logoKeyframes = `
  @keyframes logoSpringUp {
    0% { transform: translateY(150px); }
    60% { transform: translateY(-10px); }
    80% { transform: translateY(5px); }
    100% { transform: translateY(0); }
  }
  @keyframes logoDiveDown {
    0% { transform: translateY(0); }
    40% { transform: translateY(-15px); }
    100% { transform: translateY(160px); }
  }
  @keyframes logoFloat {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-6px); }
  }
  @keyframes logoOpacityLeft {
    0%, 60% { opacity: 1; }
    60.1%, 90% { opacity: 0; }
    90.1%, 100% { opacity: 1; }
  }
  @keyframes logoOpacityRight {
    0%, 60% { opacity: 0; }
    60.1%, 90% { opacity: 1; }
    90.1%, 100% { opacity: 0; }
  }
  @keyframes logoSplash {
    0% { opacity: 0; transform: scale(0.8) translateY(5px); }
    20% { opacity: 1; }
    100% { opacity: 0; transform: scale(1.3) translateY(-15px); }
  }
  @keyframes logoBlinkTop {
    0%, 90%, 100% { transform: translateY(-17px); }
    95% { transform: translateY(0); }
  }
  @keyframes logoBlinkBottom {
    0%, 90%, 100% { transform: translateY(17px); }
    95% { transform: translateY(0); }
  }
  @keyframes logoFadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  [data-logo-periscope] { transform: translateY(150px); }
  [data-logo-periscope="entering"] { animation: logoSpringUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }
  [data-logo-periscope="idle"] { transform: translateY(0); }
  [data-logo-periscope="exiting"] { animation: logoDiveDown 0.6s cubic-bezier(0.6, -0.28, 0.735, 0.045) forwards; }
  [data-logo-body] { animation: logoFloat 3s ease-in-out infinite; animation-delay: 0.8s; }
  [data-logo-view="left"] { animation: logoOpacityLeft 4s linear infinite; }
  [data-logo-view="right"] { animation: logoOpacityRight 4s linear infinite; }
  [data-logo-eyelid="top"] { animation: logoBlinkTop 2s infinite; }
  [data-logo-eyelid="bottom"] { animation: logoBlinkBottom 2s infinite; }
  [data-logo-splash] { opacity: 0; transform-origin: center bottom; }
  [data-logo-splash="enter"] { animation: logoSplash 0.6s ease-out 0.2s forwards; }
  [data-logo-splash="exit"] { animation: logoSplash 0.6s ease-out 0.35s forwards; }
  [data-logo-water] { opacity: 0; }
  [data-logo-water="visible"] { opacity: 1; }
  [data-logo-water="exiting"] { opacity: 1; animation: logoFadeOut 0.3s ease-out 0.55s forwards; }
`;

export const IconLogo = ({
  animated = false,
  animationState = 'idle',
  height = 23,
  style,
  width = 24,
}: IconLogoProps) => {
  const getPeriscopeData = () => {
    if (!animated) return undefined;
    return animationState;
  };

  const getSplashData = (type: 'enter' | 'exit') => {
    if (!animated) return 'hidden';
    if (type === 'enter' && animationState === 'entering') return 'enter';
    if (type === 'exit' && animationState === 'exiting') return 'exit';
    return 'hidden';
  };

  const getWaterData = () => {
    if (!animated) return undefined;
    if (animationState === 'exiting') return 'exiting';
    if (animationState === 'entering' || animationState === 'idle') return 'visible';
    return undefined;
  };

  return (
    <>
      {animated ? <style>{logoKeyframes}</style> : null}
      <svg
        fill="none"
        height={height}
        style={{ overflow: 'visible', ...style }}
        viewBox="0 0 100 127"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <clipPath id="icon-logo-water-mask">
            <rect height="218" width="200" x="-50" y="-100" />
          </clipPath>
          <clipPath id="icon-logo-eye-clip">
            <circle cx="0" cy="0" r="11.5" />
          </clipPath>
        </defs>

        {/* ENTRANCE SPLASH */}
        <g data-logo-splash={getSplashData('enter')} stroke="#292929" strokeLinecap="round" strokeWidth="4">
          <path d="M-2 120 L-6 112" />
          <path d="M-5 122 L-11 119" />
          <path d="M3 120 L5 112" />
          <path d="M102 120 L106 112" />
          <path d="M105 122 L111 119" />
          <path d="M97 120 L95 112" />
        </g>

        {/* EXIT SPLASH */}
        <g data-logo-splash={getSplashData('exit')} stroke="#292929" strokeLinecap="round" strokeWidth="4">
          <path d="M-2 120 L-6 112" />
          <path d="M-5 122 L-11 119" />
          <path d="M3 120 L5 112" />
          <path d="M102 120 L106 112" />
          <path d="M105 122 L111 119" />
          <path d="M97 120 L95 112" />
        </g>

        {/* PERISCOPE */}
        <g clipPath="url(#icon-logo-water-mask)">
          <g data-logo-periscope={getPeriscopeData()}>
            <g data-logo-body={animated ? '' : undefined}>
              {/* View: LEFT */}
              <g data-logo-view={animated ? 'left' : undefined}>
                {/* Eye (Behind) */}
                <g transform="translate(16.44, 16.44)">
                  <circle cx="0" cy="0" fill="white" r="11.5" />
                  <g clipPath="url(#icon-logo-eye-clip)">
                    <rect
                      data-logo-eyelid={animated ? 'top' : undefined}
                      fill="#292929"
                      height="17"
                      width="30"
                      x="-15"
                      y="-17"
                    />
                    <rect
                      data-logo-eyelid={animated ? 'bottom' : undefined}
                      fill="#292929"
                      height="17"
                      width="30"
                      x="-15"
                      y="0"
                    />
                  </g>
                </g>
                {/* Stem */}
                <path
                  d="M88.8927 97.6428C88.4774 97.2597 87.9882 96.9654 87.4552 96.7779C86.9221 96.5905 86.3564 96.5139 85.7927 96.5528H66.3827V16.4529C66.3827 14.2939 65.9575 12.1562 65.1313 10.1616C64.3051 8.16697 63.0941 6.34464 61.5675 4.82804C60.0409 3.30145 58.2286 2.09049 56.234 1.2643C54.3667 0.49082 52.1134 0.0668936 50.062 0.0159527C47.9851 0.0555063 45.6907 0.476641 43.7943 1.26216C41.7997 2.08835 39.9874 3.29931 38.4608 4.82591C36.9342 6.3525 35.7232 8.16483 34.8971 10.1594C34.0709 12.154 33.6456 14.2918 33.6456 16.4507L33.6427 96.5528H14.2527C13.686 96.5147 13.1175 96.5916 12.5813 96.7789C12.0451 96.9662 11.5523 97.2601 11.1327 97.6428C10.7386 98.0559 10.4353 98.5469 10.2424 99.0843C10.0495 99.6216 9.97129 100.193 10.0127 100.763V102.393C9.97377 102.957 10.0504 103.522 10.2378 104.055C10.4252 104.588 10.7195 105.078 11.1027 105.493C11.521 105.878 12.0136 106.173 12.5501 106.36C13.0866 106.548 13.6558 106.623 14.2227 106.583H85.7927C86.3566 106.624 86.923 106.549 87.4564 106.361C87.9898 106.174 88.4787 105.878 88.8927 105.493C89.2835 105.083 89.5828 104.594 89.7707 104.06C89.9586 103.526 90.0309 102.957 89.9827 102.393V100.763C90.0262 100.196 89.9519 99.6257 89.7643 99.0888C89.5767 98.5518 89.2799 98.0595 88.8927 97.6428Z"
                  fill="#292929"
                />
                {/* Head with hole */}
                <path
                  clipRule="evenodd"
                  d="M66.37 16.44C66.37 14.2811 65.9448 12.1433 65.1186 10.1487C64.2924 8.15409 63.0814 6.34176 61.5548 4.81516C60.0282 3.28857 58.2159 2.07761 56.2213 1.25142C54.2267 0.425234 52.0889 0 49.93 0H16.44C12.0798 0 7.89826 1.73207 4.81516 4.81516C1.73207 7.89826 4.27467e-10 12.0798 4.27463e-10 16.44C4.27466e-10 20.8002 1.73207 24.9817 4.81516 28.0648C7.89826 31.1479 12.0798 32.88 16.44 32.88H66.37V16.44ZM10.0954 25.9354C11.9734 27.1902 14.1813 27.86 16.44 27.86C19.468 27.8574 22.3711 26.6533 24.5122 24.5122C26.6533 22.3711 27.8574 19.468 27.86 16.44C27.86 14.1813 27.1902 11.9734 25.9354 10.0954C24.6805 8.21738 22.897 6.75365 20.8102 5.8893C18.7235 5.02494 16.4273 4.79879 14.2121 5.23943C11.9968 5.68008 9.96196 6.76773 8.36484 8.36484C6.76772 9.96196 5.68008 11.9968 5.23943 14.2121C4.79879 16.4273 5.02494 18.7235 5.8893 20.8102C6.75365 22.897 8.21738 24.6805 10.0954 25.9354Z"
                  fill="#292929"
                  fillRule="evenodd"
                />
              </g>

              {/* View: RIGHT */}
              <g data-logo-view={animated ? 'right' : undefined}>
                {/* Eye */}
                <g transform="translate(83.56, 16.44)">
                  <circle cx="0" cy="0" fill="white" r="11.5" />
                  <g clipPath="url(#icon-logo-eye-clip)">
                    <rect
                      data-logo-eyelid={animated ? 'top' : undefined}
                      fill="#292929"
                      height="17"
                      width="30"
                      x="-15"
                      y="-17"
                    />
                    <rect
                      data-logo-eyelid={animated ? 'bottom' : undefined}
                      fill="#292929"
                      height="17"
                      width="30"
                      x="-15"
                      y="0"
                    />
                  </g>
                </g>
                {/* Stem */}
                <path
                  d="M88.8927 97.6321C88.4774 97.2489 87.9882 96.9546 87.4552 96.7672C86.9221 96.5798 86.3564 96.5032 85.7927 96.5421H66.3827V16.4421C66.3827 14.2832 65.9575 12.1562 65.1313 10.1508C64.3051 8.15623 63.0941 6.34389 61.5675 4.8173C60.0409 3.2907 58.2286 2.07975 56.234 1.25356C54.3667 0.480078 52.1134 0.0561515 50.062 0.00521055C47.9851 0.0447641 45.6907 0.465899 43.7943 1.25142C41.7997 2.07761 39.9874 3.28857 38.4608 4.81516C36.9342 6.34176 35.7232 8.15409 34.8971 10.1487C34.0709 12.1433 33.6456 14.2811 33.6456 16.44L33.6427 96.5421H14.2527C13.686 96.5039 13.1175 96.5808 12.5813 96.7682C12.0451 96.9555 11.5523 97.2494 11.1327 97.6321C10.7386 98.0452 10.4353 98.5362 10.2424 99.0735C10.0495 99.6109 9.97129 100.183 10.0127 100.752V102.382C9.97377 102.946 10.0504 103.512 10.2378 104.045C10.4252 104.578 10.7195 105.067 11.1027 105.482C11.521 105.867 12.0136 106.162 12.5501 106.349C13.0866 106.537 13.6558 106.613 14.2227 106.572H85.7927C86.3566 106.613 86.923 106.538 87.4564 106.35C87.9898 106.163 88.4787 105.867 88.8927 105.482C89.2835 105.072 89.5828 104.584 89.7707 104.049C89.9586 103.515 90.0309 102.947 89.9827 102.393V100.752C90.0262 100.185 89.9519 99.615 89.7643 99.078C89.5767 98.5411 89.2799 98.0487 88.8927 97.6321Z"
                  fill="#292929"
                />
                {/* Head with hole (Mirrored) */}
                <path
                  clipRule="evenodd"
                  d="M66.37 16.44C66.37 14.2811 65.9448 12.1433 65.1186 10.1487C64.2924 8.15409 63.0814 6.34176 61.5548 4.81516C60.0282 3.28857 58.2159 2.07761 56.2213 1.25142C54.2267 0.425234 52.0889 0 49.93 0H16.44C12.0798 0 7.89826 1.73207 4.81516 4.81516C1.73207 7.89826 4.27467e-10 12.0798 4.27463e-10 16.44C4.27466e-10 20.8002 1.73207 24.9817 4.81516 28.0648C7.89826 31.1479 12.0798 32.88 16.44 32.88H66.37V16.44ZM10.0954 25.9354C11.9734 27.1902 14.1813 27.86 16.44 27.86C19.468 27.8574 22.3711 26.6533 24.5122 24.5122C26.6533 22.3711 27.8574 19.468 27.86 16.44C27.86 14.1813 27.1902 11.9734 25.9354 10.0954C24.6805 8.21738 22.897 6.75365 20.8102 5.8893C18.7235 5.02494 16.4273 4.79879 14.2121 5.23943C11.9968 5.68008 9.96196 6.76773 8.36484 8.36484C6.76772 9.96196 5.68008 11.9968 5.23943 14.2121C4.79879 16.4273 5.02494 18.7235 5.8893 20.8102C6.75365 22.897 8.21738 24.6805 10.0954 25.9354Z"
                  fill="#292929"
                  fillRule="evenodd"
                  transform="translate(100, 0) scale(-1, 1)"
                />
              </g>
            </g>
          </g>
        </g>

        {/* WATER BASE */}
        <path
          d="M100.013 120.823V122.453C100.062 123.019 99.9908 123.589 99.8029 124.125C99.6149 124.662 99.3149 125.152 98.9227 125.563C98.5086 125.947 98.0194 126.242 97.4858 126.428C96.9523 126.613 96.386 126.687 95.8227 126.643H4.2227C3.65652 126.685 3.08775 126.61 2.55126 126.425C2.01477 126.239 1.52185 125.946 1.1027 125.563C0.719261 125.146 0.424853 124.655 0.237447 124.12C0.0500417 123.585 -0.0264297 123.018 0.0127011 122.453V120.823C-0.0320496 120.256 0.0417512 119.685 0.229408 119.148C0.417065 118.611 0.71447 118.119 1.1027 117.703C1.52234 117.32 2.01508 117.026 2.55127 116.839C3.08746 116.652 3.65601 116.575 4.2227 116.613H95.8227C96.3899 116.551 96.9638 116.617 97.5018 116.807C98.0399 116.997 98.5283 117.305 98.9308 117.71C99.3333 118.114 99.6396 118.604 99.8269 119.143C100.014 119.682 100.078 120.256 100.013 120.823Z"
          data-logo-water={getWaterData()}
          fill="#292929"
        />
      </svg>
    </>
  );
};
